import * as v from 'valibot'
import { NovelPlaytestDiagnostics } from './runs.js'

// 快照里的正文段、场景、候选、任务等是 Demo 引擎的结构，字段随引擎走；契约只钉住外层形状，内层整体透传。
const Json = v.record(v.string(), v.unknown())
const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(80))
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))

/**
 * 对局视图里的一个表演状态：作品内容原样透传（id、description、image 等），另带 drama_ready（勘误 26）——非玩家人物且状态图是
 * https 公网地址时为 true，与建会话的校验是同一条规则；玩家端只读它决定能否由数字人演。
 */
const NovelRunPerformanceState = v.intersect([Json, v.object({ id: v.string(), drama_ready: v.boolean() })])
/** 对局视图里的一个人物：作品内容原样透传（id、name、image），每个表演状态带 drama_ready。 */
const NovelRunCharacter = v.intersect([Json, v.object({ id: v.string(), performance_states: v.array(NovelRunPerformanceState) })])

/**
 * 一回合的输入。request_id 是 UUID：账本幂等键由 run_id 与 request_id 拼成，上限 191 字符。
 * @typedef {{ request_id: string, branch_id: string, parent_seq: number, mode: 'mixed'|'speech'|'action'|'continue', text: string }} NovelTurnBodyT
 */
export const NovelTurnBody = v.strictObject({
  request_id: v.pipe(v.string(), v.uuid()),
  branch_id: Id,
  parent_seq: Count,
  mode: v.picklist(['mixed', 'speech', 'action', 'continue']),
  // 形态 4.8：一回合输入上限 1000 字（M21）。
  text: v.pipe(v.string(), v.maxLength(1000)),
})

/**
 * 对局视图里活动分支的一个后台任务（记录、记忆、导演、建议）。attempts 是已执行次数；失败后自动重排的任务留在 pending，
 * next_retry_at 是它下次可执行的时间，不在等待重试时为 null；error_code 是最近一次失败的原因码（C26）。
 */
export const NovelRunJob = v.object({
  job_id: v.string(),
  kind: v.picklist(['narrative_record', 'memory', 'director', 'suggestions']),
  status: v.picklist(['pending', 'completed', 'failed', 'superseded']),
  through_seq: Count,
  error_code: v.nullable(v.string()),
  structured_model_id: v.nullable(v.string()),
  model: v.nullable(v.string()),
  attempts: Count,
  next_retry_at: v.nullable(v.string()),
  trigger: v.optional(v.string()),
  director_revision: v.optional(v.nullable(Count)),
})

/**
 * 付费动作单价（C23，勘误 15）：turn_points 取动作定价 novel_turn，缺行或停用为 0。配音没有动作定价行，走平台模型结算，
 * 按 TTS 计费策略（MODEL_BILLING_MODE）定：record_only 为 0 且 tts_metered = false；fixed_points 为每段（合成一次）
 * 的固定积分且 tts_metered = false；cost_ratio 单价随字数变，tts_points = 0 且 tts_metered = true（按字数扣积分）。
 * tts_available 为配音可用：没接 TTS 或没有启用的音色时为 false，玩家端隐藏自动朗读开关与朗读按钮（R-7）。
 */
export const NovelRunPrices = v.object({
  turn_points: Count,
  tts_points: Count,
  tts_metered: v.boolean(),
  tts_available: v.boolean(),
  // 数字人（C28，勘误 23）：四条同时满足才为 true——avatar:vidu 与 rtc:artc 各至少一条启用的凭据、动作 novel_drama_session 有定价行且启用、
  // tts_available 为 true、作品至少一个表演状态 drama_ready；为 false 时玩家端不渲染开关。
  drama_available: v.boolean(),
  drama_v_available: v.optional(v.boolean(), false),
  // 动作 novel_drama_session 的单价（一回合一场），停用或缺行为 0。
  drama_points: Count,
})

/**
 * 对局视图：Demo `get_run` 的视图（模型字段是模式名）；回合与重生成的返回另带本回合的 charged_points 与 model。
 * status 是对局状态（归档的对局只读，C1）。`GET /api/novel/runs/:id?history=none` 返回同一视图但 history 为空、
 * history_omitted 为 true，供轮询只看状态；其余视图 history_omitted 为 false。
 */
export const NovelRunView = v.object({
  play_language: v.string(),
  purpose: v.picklist(['play', 'playtest', 'comparison']),
  source_origin: v.string(),
  diagnostics: v.optional(NovelPlaytestDiagnostics),
  run_id: v.string(),
  status: v.picklist(['active', 'archived']),
  title: v.string(),
  opening_cg: v.optional(v.nullable(v.object({
    pack_id: v.string(), version_id: v.string(), package_hash: v.string(), play_config_hash: v.string(),
    opening_hash: v.string(), voice_render_hash: v.string(), video_url: v.string(), poster_url: v.string(),
    captions_url: v.string(), video_sha256: v.string(), duration_ms: Count, hook: v.string(),
  }))),
  package_version: v.unknown(),
  performer_id: v.string(),
  structured_model_id: v.string(),
  narrative_mode: v.boolean(),
  recording: v.object({ through_seq: Count, pending_count: Count, failed_count: Count }),
  director: v.nullable(Json),
  active_branch_id: v.string(),
  player_id: v.string(),
  characters: v.array(NovelRunCharacter),
  narrator_tts_available: v.optional(v.boolean(), false),
  locations: v.optional(v.array(Json), []),
  performance_selection: v.nullable(Json),
  current_scene: Json,
  candidates: v.array(Json),
  story_time: v.unknown(),
  branches: v.array(v.object({ branch_id: v.string(), parent_branch_id: v.nullable(v.string()), fork_seq: Count, turn_count: Count })),
  history: v.array(Json),
  history_omitted: v.boolean(),
  // 当前舞台的玩家可见段落；history=none 的轮询同样携带，续玩与切分支不依赖旧历史推断。
  stage_segments: v.array(Json),
  turn_count: Count,
  player_suggestions: v.nullable(v.object({
    generation_id: v.string(),
    turn_id: v.string(),
    through_seq: Count,
    status: v.picklist(['pending', 'ready', 'failed']),
    items: v.array(v.string()),
  })),
  requests: v.array(Json),
  jobs: v.array(NovelRunJob),
  preferences: v.array(Json),
  preview: v.nullable(Json),
  prices: NovelRunPrices,
  charged_points: v.optional(Count),
  model: v.optional(v.nullable(v.string())),
  // 形态 4.8：链内回退成功（主力失败、备用成功）时为 true，界面提示已切换模型。
  model_switched: v.optional(v.boolean()),
})

/**
 * 回合 NDJSON 事件，顺序 phase(generating) → preview([]) → stage → preview* → result；出错时以 error 结束。
 * error_code 是小写原因码（insufficient_balance 另带计费详情）。同一 request_id 已在生成（本进程或别的进程）时
 * 只发 pending 并结束流，不发 result（C2）：调用方转为轮询对局视图等它落定。
 */
export const NovelTurnEvent = v.union([
  v.object({ type: v.literal('pending'), request_id: v.string() }),
  v.object({ type: v.literal('phase'), request_id: v.string(), phase: v.string() }),
  v.object({ type: v.literal('preview'), request_id: v.string(), segments: v.array(Json), provisional: v.boolean() }),
  v.object({ type: v.literal('stage'), request_id: v.string(), selection: v.nullable(Json) }),
  v.object({ type: v.literal('result'), run: NovelRunView }),
  v.object({
    type: v.literal('error'),
    error_code: v.string(),
    error: v.optional(v.string()),
    action: v.optional(v.string()),
    required_points: v.optional(v.number()),
    current_balance: v.optional(v.number()),
    shortfall_points: v.optional(v.number()),
  }),
])
